
// src/app/tools/blog-idea-generator/page.tsx
'use client';

import { useState } from 'react';
import { Copy, RefreshCw, Search, AlertCircle, Check, BookOpen } from 'lucide-react';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { blogIdeaGeneratorContent } from '../../data/blog-idea-generator-content';

export default function BlogIdeaGenerator() {
    const [keyword, setKeyword] = useState('');
    const [niche, setNiche] = useState('');
    const [tone, setTone] = useState('informative');
    const [ideas, setIdeas] = useState<string[]>([]);
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState('');
    const [copied, setCopied] = useState<number | null>(null);

    const tones = [
        { value: 'informative', label: 'Informative' },
        { value: 'casual', label: 'Casual' },
        { value: 'professional', label: 'Professional' },
        { value: 'humorous', label: 'Humorous' },
        { value: 'inspirational', label: 'Inspirational' },
    ];

    const generateIdeas = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!keyword.trim()) {
            setError('Please enter a keyword or topic');
            return;
        }

        setIsGenerating(true);
        setError('');

        try {
            const response = await fetch('/api/generate/blog-ideas', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    keyword,
                    niche: niche || undefined,
                    tone,
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to generate blog ideas');
            }

            const data = await response.json();
            setIdeas(data.ideas);
        } catch (err) {
            console.error('Error generating blog ideas:', err);
            setError('Failed to generate blog ideas. Please try again.');
            setIdeas(generateMockIdeas(keyword, niche, tone));
        } finally {
            setIsGenerating(false);
        }
    };

    const copyToClipboard = (text: string, index: number) => {
        navigator.clipboard.writeText(text);
        setCopied(index);
        setTimeout(() => setCopied(null), 2000);
    };

    const resetForm = () => {
        setKeyword('');
        setNiche('');
        setTone('informative');
        setIdeas([]);
        setError('');
    };

    return (

        <div className="max-w-4xl mx-auto p-6">
            <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                    <BookOpen className="h-8 w-8 text-blue-600" />
                </div>
                <ToolPageIntro title="Blog Idea Generator">
                Generate blog post ideas instantly with our free online tool. Get creative content suggestions for any niche or topic.
            </ToolPageIntro>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
                <form onSubmit={generateIdeas} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label htmlFor="keyword" className="block text-sm font-medium text-gray-700 mb-1">
                                Main Topic or Keyword * <span className="text-xs text-gray-500">(required)</span>
                            </label>
                            <input
                                type="text"
                                id="keyword"
                                value={keyword}
                                onChange={(e) => setKeyword(e.target.value)}
                                placeholder="e.g., digital marketing, healthy recipes"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                required
                            />
                        </div>

                        <div>
                            <label htmlFor="niche" className="block text-sm font-medium text-gray-700 mb-1">
                                Niche or Industry <span className="text-xs text-gray-500">(optional)</span>
                            </label>
                            <input
                                type="text"
                                id="niche"
                                value={niche}
                                onChange={(e) => setNiche(e.target.value)}
                                placeholder="e.g., fitness, technology, finance"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                        </div>

                        <div>
                            <label htmlFor="tone" className="block text-sm font-medium text-gray-700 mb-1">
                                Writing Tone
                            </label>
                            <select
                                id="tone"
                                value={tone}
                                onChange={(e) => setTone(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                                {tones.map((toneOption) => (
                                    <option key={toneOption.value} value={toneOption.value}>
                                        {toneOption.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {error && (
                        <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <AlertCircle className="h-5 w-5 text-red-400" />
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm text-red-700">{error}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3 pt-2">
                        <button
                            type="button"
                            onClick={resetForm}
                            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            Reset
                        </button>
                        <button
                            type="submit"
                            disabled={isGenerating}
                            className={`px-6 py-2 rounded-lg text-white font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${isGenerating
                                ? 'bg-blue-400 cursor-not-allowed'
                                : 'bg-blue-600 hover:bg-blue-700'
                                }`}
                        >
                            {isGenerating ? (
                                <>
                                    <RefreshCw className="animate-spin h-5 w-5 mr-2 inline" />
                                    Generating...
                                </>
                            ) : (
                                'Generate Ideas'
                            )}
                        </button>
                    </div>
                </form>
            </div>

            {ideas.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm p-6 space-y-6">
                    <div className="flex items-center justify-between">
                        <h2 className="text-xl font-semibold text-gray-900">Generated Blog Ideas</h2>
                        <span className="text-sm text-gray-500">{ideas.length} ideas</span>
                    </div>

                    <div className="space-y-4">
                        {ideas.map((idea, index) => (
                            <div
                                key={index}
                                className="group relative p-4 border border-gray-200 rounded-lg hover:border-blue-200 hover:bg-blue-50 transition-colors"
                            >
                                <p className="text-gray-800 pr-8">{idea}</p>
                                <button
                                    onClick={() => copyToClipboard(idea, index)}
                                    className="absolute top-3 right-3 p-1 text-gray-400 hover:text-blue-600 rounded-full hover:bg-blue-100"
                                    title="Copy to clipboard"
                                >
                                    {copied === index ? (
                                        <Check className="h-4 w-4 text-green-500" />
                                    ) : (
                                        <Copy className="h-4 w-4" />
                                    )}
                                </button>
                            </div>
                        ))}
                    </div>

                    <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-md">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg
                                    className="h-5 w-5 text-blue-400"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-blue-800">Tips for Great Blog Ideas</h3>
                                <div className="mt-2 text-sm text-blue-700 space-y-1">
                                    <p>• Use specific, long-tail keywords for better targeting</p>
                                    <p>• Consider your audience's pain points and questions</p>
                                    <p>• Mix different content types (how-tos, lists, case studies)</p>
                                    <p>• Stay updated with industry trends and news</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* SEO CONTENT */}
            <div className="mt-16">
                <InformationalContent
                    title={blogIdeaGeneratorContent.title}
                    description={blogIdeaGeneratorContent.description}
                    content={blogIdeaGeneratorContent.content}
                    faqs={blogIdeaGeneratorContent.faqs}
                    relatedTools={blogIdeaGeneratorContent.relatedTools}
                />
            </div>
        </div>
    );
}

// Mock data generators for development
function generateMockIdeas(keyword: string, niche?: string, tone: string = 'informative'): string[] {
    const baseIdeas = [
        `The Ultimate Guide to ${keyword} in ${new Date().getFullYear()}`,
        `10 Best ${keyword} Strategies for ${niche || 'Your Industry'}`,
        `How to Master ${keyword}: A Step-by-Step Guide`,
        `The Top 5 ${keyword} Trends to Watch This Year`,
        `Beginner's Guide to ${keyword} for ${niche || 'Newcomers'}`,
        `Why ${keyword} is Important for ${niche || 'Your Business'}`,
        `${keyword} vs. [Related Topic]: Key Differences Explained`,
        `How to Solve Common ${keyword} Problems in ${niche || 'Your Field'}`,
        `The Complete ${keyword} Checklist for ${niche || 'Success'}`,
        `Expert Tips for Better ${keyword} in ${niche || 'Your Work'}`,
    ];

    // Adjust tone
    if (tone === 'casual') {
        return baseIdeas.map(idea =>
            idea
                .replace('The Ultimate Guide', 'Your Complete Guide')
                .replace('Strategies', 'Ways')
                .replace('Master', 'Get Really Good At')
                .replace('Important', 'a Game-Changer')
        );
    }

    if (tone === 'professional') {
        return baseIdeas.map(idea =>
            idea
                .replace('Guide to', 'Comprehensive Analysis of')
                .replace('How to', 'A Systematic Approach to')
                .replace('Best', 'Optimal')
                .replace('Tips', 'Recommendations')
        );
    }

    if (tone === 'humorous') {
        return baseIdeas.map(idea =>
            idea
                .replace('Guide', 'Survival Guide')
                .replace('Strategies', 'Secrets Your Competitors Don\'t Want You to Know')
                .replace('How to Master', 'How to Not Screw Up')
        );
    }

    if (tone === 'inspirational') {
        return baseIdeas.map(idea =>
            idea
                .replace('Guide', 'Journey')
                .replace('How to', 'Transforming Your Approach to')
                .replace('Best', 'Most Empowering')
                .replace('Tips', 'Inspiring Insights')
        );
    }

    return baseIdeas;
}